/**
 * Os dados do painel do documento original (E201.F2, GUS-47, ADR-0144 §8): os documentos do caso que
 * se abrem aqui (PDF e imagem) e o arquivo original do escolhido, pela rota que confere que o
 * documento é do caso e não entrega o guardado que não confere com o recebido (E65.F6, E184.F4).
 * O original nunca é alterado: a tela só lê os bytes e os mostra.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import {
  DocumentError,
  listDocuments,
  type SourceDocumentRecordData,
} from '@/lib/case_source_documents';
import { visualizarArquivoOriginal } from '@/lib/abrir_original';

export type DocumentosDoPainel =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | {
      estado: 'pronto';
      /** Os que abrem no painel (PDF e imagem), a CCB primeiro. */
      documentos: SourceDocumentRecordData[];
      /** Todos os do caso, para dizer o nome do pedido que não abre aqui (E201.F2.C01). */
      todos: SourceDocumentRecordData[];
      /** O caso da lista: a releitura do mesmo caso mantém a lista à vista enquanto lê. */
      de: string;
    };

/**
 * E205.F4.T03: por que o arquivo não abriu — a custódia que não confere (409, não se mostra por
 * desenho), o arquivo que não está mais no caso (404), o recorte do acervo (403) ou outra falha (a
 * rede, o servidor), a única que se tenta de novo.
 */
export type MotivoDoArquivo = 'divergente' | 'nao_encontrado' | 'fora_do_recorte' | 'falha';

export type ArquivoDoPainel =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDoArquivo }
  | { estado: 'pronto'; url: string };

const MOTIVO_DA_RECUSA: Partial<Record<string, MotivoDoArquivo>> = {
  original_divergente: 'divergente',
  document_not_found: 'nao_encontrado',
  case_not_found: 'nao_encontrado',
  fora_do_recorte: 'fora_do_recorte',
};

export function motivoDoArquivo(err: unknown): MotivoDoArquivo {
  if (!(err instanceof DocumentError)) return 'falha';
  return MOTIVO_DA_RECUSA[err.reason] ?? 'falha';
}

export function ehPdf(doc: SourceDocumentRecordData): boolean {
  return doc.mime_type === 'application/pdf';
}

/** O painel mostra PDF e imagem; o resto (DOCX, ZIP, e-mail, HTML) se baixa. */
export function tipoQueAbre(doc: SourceDocumentRecordData): boolean {
  return ehPdf(doc) || doc.mime_type.startsWith('image/');
}

export function abreNoPainel(doc: SourceDocumentRecordData): boolean {
  return doc.deleted_at == null && tipoQueAbre(doc);
}

export function nomeDoDocumento(doc: SourceDocumentRecordData): string {
  const nome = doc.nome_do_documento?.trim();
  return nome !== undefined && nome !== '' ? nome : doc.original_filename;
}

/**
 * A CCB primeiro (o documento questionado), depois os demais na ordem em que chegaram. GUS-296 item
 * 3: na raia eletrônica todo arquivo é `contested`; a imagem (a selfie, a foto do documento) não é a
 * CCB e não vai à frente.
 */
export function emOrdem(docs: SourceDocumentRecordData[]): SourceDocumentRecordData[] {
  const naFrente = (d: SourceDocumentRecordData) =>
    d.kind === 'contested' && !d.mime_type.startsWith('image/');
  return [...docs.filter(naFrente), ...docs.filter((d) => !naFrente(d))];
}

/**
 * O arquivo que o painel mostra (E201.F2.C01, F-E201.F2-01): o pedido; o primeiro da lista só quando
 * nada foi pedido. O pedido que não abre aqui não vira outro arquivo: volta `undefined` e a tela avisa.
 */
export function documentoDoPainel(
  documentos: readonly SourceDocumentRecordData[],
  pedido: string | null,
): SourceDocumentRecordData | undefined {
  if (pedido === null) return documentos[0];
  return documentos.find((d) => d.id === pedido);
}

/**
 * Os documentos do caso. Muda `releitura` (a nova tentativa, o caso relido): lê de novo, e a lista do
 * mesmo caso fica à vista enquanto lê — a aba Documentos não pisca a cada releitura (E205.F4).
 */
export function useDocumentosDoPainel(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): DocumentosDoPainel {
  const [lista, setLista] = useState<DocumentosDoPainel>({ estado: 'carregando' });
  useEffect(() => {
    let vivo = true;
    setLista((atual) =>
      atual.estado === 'pronto' && atual.de === caseId ? atual : { estado: 'carregando' },
    );
    void listDocuments(client, caseId)
      .then((docs) => {
        if (!vivo) return;
        const documentos = emOrdem(docs.filter(abreNoPainel));
        setLista({ estado: 'pronto', documentos, todos: docs, de: caseId });
      })
      .catch(() => {
        if (vivo) setLista({ estado: 'erro' });
      });
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura]);
  return lista;
}

export function useArquivoDoPainel(
  client: AxiosInstance,
  caseId: string,
  doc: SourceDocumentRecordData | null,
  tentativa: number,
): ArquivoDoPainel {
  const [arquivo, setArquivo] = useState<ArquivoDoPainel>({ estado: 'carregando' });
  const docId = doc?.id ?? null;
  useEffect(() => {
    if (docId === null) return undefined;
    let vivo = true;
    let criada: string | null = null;
    setArquivo({ estado: 'carregando' });
    // E205.F4.C03: mostrar é visualizar (quem enxerga o caso), não baixar pelo recorte do acervo.
    void visualizarArquivoOriginal(client, caseId, docId)
      .then((blob) => {
        criada = URL.createObjectURL(blob);
        if (vivo) setArquivo({ estado: 'pronto', url: criada });
      })
      .catch((err: unknown) => {
        if (vivo) setArquivo({ estado: 'erro', motivo: motivoDoArquivo(err) });
      });
    return () => {
      vivo = false;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, docId, tentativa]);
  return arquivo;
}
